import { ContentContainer, LoadingIndicator, useTitleChange } from '@msaf/core-react'
import * as L from 'leaflet'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import MapPinWhite from '../../../assets/map-pin-solid-white.svg'
import mapboxLogo from '../../../mapbox-logo.svg'
import { getLayersConfig } from '../../config/map'
import useAdvancedSearch from '../../hooks/use-advanced-search'
import useUser from '../../hooks/use-user'
import { getRouteForWellStatus } from '../../routes/utils/route-utils'
import { authButtonHandlerFactory, isValidToken } from '../../services/auth-utils'
import { DISPLAY_DATE_FORMAT, SearchTypeKey } from '../../services/constants'
import {
  CUSTOM_ICON_CONFIG,
  customIconBaseOptions,
  defaultIcon,
  LEGEND_MENU_CONFIG,
  locationIcon,
  selectedIcon,
  SHOW_MARKER_LABEL_ZOOM_THRESHOLD,
} from '../../services/generic-search'
import { AdvancedSearchMap } from '../aoraki/generic-search-maps-react/src'
import { CustomIconFunction } from '../aoraki/generic-search-maps-react/src/hooks/use-map-search'
import RegisterModal from '../register-modal'
import SignInModal from '../sign-in-modal'
import WellMapPopup from './map-popup'
import { DEFAULT_ZOOM } from '../aoraki/generic-search-maps-react/src/config/constants'

interface FullScreenMapProps {
  searchKey: SearchTypeKey
  isDefaultTabbedContent?: boolean
  onTabChange?: (index: number) => void
  mapTabs?: Array<any>
  activeTab?: number
}

export default function FullScreenMap({
  searchKey = SearchTypeKey.NATIONAL,
  isDefaultTabbedContent,
  onTabChange,
  mapTabs,
  activeTab,
}: FullScreenMapProps) {
  useTitleChange('Wells Aotearoa NZ | Map search')
  const location = useLocation()
  const { user } = useUser()
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false)
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false)
  const {
    onSearchQueryChange,
    searchTypeKey,
    searchTemplate,
    requestOptions,
    cachedSearchQuery,
    onZoomLevelChange,
    cachedMapBounds,
  } = useAdvancedSearch({ searchTypeKey: searchKey })

  const dynamicTransitionAction = (id: number, wellRecordStatus: string) => {
    return getRouteForWellStatus(user, id, wellRecordStatus)
  }

  if (!searchTemplate) {
    return (
      <ContentContainer>
        <LoadingIndicator>Loading&hellip;</LoadingIndicator>
      </ContentContainer>
    )
  }

  const getLabelHtml = (path: string, label?: string | number | boolean) => {
    return `<div class="c-marker-container"><img src="${path}"><div class="c-marker__icon-label">${
      label ? label : ''
    }</div></div>`
  }

  const getIcon = (iconUrl: string, label?: string | number | boolean) => {
    const iconOptions = { ...customIconBaseOptions, iconUrl, html: getLabelHtml(iconUrl, label) }
    return new L.DivIcon(iconOptions)
  }

  const iconSelected = (icon: L.DivIcon) => {
    icon.options.iconSize = selectedIcon.options.iconSize
    icon.options.iconAnchor = selectedIcon.options.iconAnchor
    return icon
  }

  const onZoomLevelChangeCustom = (map: L.Map) => {
    onZoomLevelChange(map.getBounds())
    const currentMapZoom = map.getZoom()
    if (currentMapZoom <= SHOW_MARKER_LABEL_ZOOM_THRESHOLD) {
      map.getContainer().classList.add('c-marker-icon__hide-label')
    } else if (currentMapZoom > SHOW_MARKER_LABEL_ZOOM_THRESHOLD) {
      map.getContainer().classList.remove('c-marker-icon__hide-label')
    }
  }

  const customIconFunction: CustomIconFunction = (searchResult, isSelected) => {
    const iconDepth = searchResult[CUSTOM_ICON_CONFIG.searchKey]

    // Check if a conditional Icon condition exists for the searchKey
    const iconColor = CUSTOM_ICON_CONFIG.customIconConditions.find(
      (c) => Number(iconDepth) >= c.minLimit && Number(iconDepth) <= c.maxLimit,
    )?.color
    const getIconColor = iconDepth && iconColor ? iconColor : MapPinWhite
    const icon = getIcon(getIconColor, searchResult[CUSTOM_ICON_CONFIG.labelKey])
    if (isSelected) {
      return iconSelected(icon)
    }

    return icon
  }

  // HACK: Unfortunately, the msaf library sets the default baseLayer to Open Street Maps when
  // the optional baselayer is not used.
  const emptyLayerGroup = () => L.layerGroup([])

  const addInitialLayersAndMarkersCustom = (map: L.Map) => {
    const layersConfig = getLayersConfig()
    // Sets custom leaflet attribution without the Ukrainian flag
    map.attributionControl.setPrefix(`
      <a href="https://leafletjs.com/">Leaflet</a>
      © <a href="https://www.mapbox.com/about/maps/">Mapbox</a>
      © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>
      <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>`)

    L.control.layers(layersConfig.layers, {}, { position: 'bottomright', autoZIndex: false }).addTo(map)
    map?.addLayer(layersConfig.defaultLayers)

    map?.attributionControl.remove()

    const Watermark = L.Control.extend({
      onAdd: (map: L.Map) => {
        const img = L.DomUtil.create('img')
        img.src = mapboxLogo
        img.className = 'mapbox-logo'
        return img
      },
      onRemove() {},
    })

    new L.Control.Attribution({ position: 'bottomleft' }).addTo(map)

    new Watermark({ position: 'bottomleft' }).addTo(map)
  }

  return (
    <>
      <SignInModal isOpen={isSignInModalOpen} closeModal={() => setIsSignInModalOpen(false)} />
      <RegisterModal isOpen={isRegisterModalOpen} closeModal={() => setIsRegisterModalOpen(false)} />
      <AdvancedSearchMap
        addInitialLayersAndMarkers={addInitialLayersAndMarkersCustom}
        searchTypeKey={searchTypeKey}
        requestOptions={requestOptions}
        dateFormat={DISPLAY_DATE_FORMAT}
        locationIcon={locationIcon}
        defaultIcon={defaultIcon}
        searchTemplate={searchTemplate}
        getBaseLayers={emptyLayerGroup}
        customPopupComponent={WellMapPopup}
        mapOptions={{ zoom: DEFAULT_ZOOM, center: [-41.5, 172.8] }} // NZ centre as lat/lon
        mapPanelDisplayOptions={{
          isFeaturesEnabled: false,
          isLayersEnabled: false,
        }}
        showMapPageHeader={!isValidToken(user?.token)}
        pageHeaderActionsConfig={[
          {
            key: 'signInBtn',
            buttonStyle: 'secondary',
            label: 'Sign in',
            onClick: authButtonHandlerFactory(location.pathname, setIsSignInModalOpen),
          },
          {
            key: 'signUpBtn',
            label: 'Register',
            onClick: authButtonHandlerFactory(location.pathname, setIsRegisterModalOpen),
          },
        ]}
        customActions={{ dynamicTransitionAction }}
        onSearchQueryChange={onSearchQueryChange}
        cachedSearchQuery={cachedSearchQuery}
        onZoomLevelChange={onZoomLevelChangeCustom}
        isDefaultTabbedContent={isDefaultTabbedContent}
        onTabChange={onTabChange}
        mapTabs={mapTabs}
        activeTab={activeTab}
        cachedMapBounds={cachedMapBounds}
        legendMenuConfig={LEGEND_MENU_CONFIG}
        customIconFunction={customIconFunction}
      />
    </>
  )
}
